"use client";

/**
 * ExportAnalyticsDisplay - Analytics export result display
 */

import { useState } from "react";
import { Tag, Button, Alert } from "@/ui";
import { spacing, textStyles } from "@/ui/tokens";
import { Download } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface ExportOutput {
  success?: boolean;
  status?: "success" | "error";
  downloadUrl?: string;
  filename?: string;
  format?: string;
  rowCount?: number;
  error?: string;
}

export function ExportAnalyticsDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as ExportOutput | undefined;
  // Check for success via either `success: true` or `status: "success"`
  const isSuccess = result?.success === true || result?.status === "success";

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={isLoading ? "Exporting..." : isSuccess ? "Export Ready" : "Export"}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Download style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded}>
        {isLoading && (
          <div style={{ paddingTop: spacing[4], paddingBottom: spacing[4], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Preparing export..." />
          </div>
        )}

        {isError && error && <ToolError error={error} />}

        {!isLoading && result && (
          <>
            {isSuccess ? (
              <Alert variant="success" title="Export Complete">
                <div style={{ display: "flex", gap: spacing[2], marginTop: spacing[2], flexWrap: "wrap", alignItems: "center" }}>
                  {result.filename && (
                    <Tag size="sm">{result.filename}</Tag>
                  )}
                  {result.format && (
                    <Tag size="sm" variant="default" color="primary">
                      {result.format.toUpperCase()}
                    </Tag>
                  )}
                  {result.rowCount && (
                    <Tag size="sm" variant="default" color="primary">
                      {result.rowCount} rows
                    </Tag>
                  )}
                  {result.downloadUrl && (
                    <a
                      href={result.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginLeft: "auto" }}
                    >
                      <Button size="sm" variant="secondary" startIcon={<Download />}>
                        Download
                      </Button>
                    </a>
                  )}
                </div>
              </Alert>
            ) : (
              <Alert variant="error">
                <span style={{ ...textStyles.body2 }}>
                  {result.error || "Export failed"}
                </span>
              </Alert>
            )}
          </>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default ExportAnalyticsDisplay;
